/**
 * Builder-side PDF export.
 *
 * Generates a focused "builder copy" of the snag list from a share payload.
 * Optimised for site teams: defects sorted by severity (urgent first),
 * with each row showing photo + severity + AI verdict + builder response
 * status. Footer marks every page with the date and the builder's email
 * so it doubles as an audit trail.
 *
 * Deliberately self-contained — does NOT share code with Report.jsx so
 * the existing homeowner PDF stays unchanged. ~280 lines total.
 */
import { jsPDF } from 'jspdf';

const SEV_RANK = { urgent: 0, major: 1, minor: 2, cosmetic: 3 };
const SEV_COLOR = {
    urgent:   [153, 27, 27],
    major:    [245, 158, 11],
    minor:    [31, 165, 207],
    cosmetic: [161, 161, 170],
};

function toRgb(hex) {
    const m = hex.replace('#', '');
    return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
}

function statusBadge(s) {
    if (s.builder_response === 'approved') return { label: 'APPROVED', color: [0, 135, 90] };
    if (s.builder_response === 'rejected') return { label: 'REJECTED', color: [229, 57, 53] };
    if (s.disputed) return { label: 'DISPUTED', color: [30, 58, 138] };
    if (s.status === 'completed') return { label: 'COMPLETED', color: [0, 135, 90] };
    return { label: 'AWAITING', color: [120, 120, 120] };
}

function loadImageAsDataUrl(src) {
    // Supports both data: URLs (no fetch needed) and remote photo URLs.
    return new Promise((resolve) => {
        if (!src) return resolve(null);
        if (src.startsWith('data:')) return resolve(src);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const max = 800;
                let { width, height } = img;
                if (width > max || height > max) {
                    const r = Math.min(max / width, max / height);
                    width = Math.round(width * r);
                    height = Math.round(height * r);
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', 0.7));
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

/**
 * Generate a builder-copy PDF from a share payload.
 *
 * @param {object} payload  - data returned from GET /api/share/{shortId}
 * @param {object} meta     - { builderName, builderEmail, downloadedAt }
 * @returns {Promise<{url: string, filename: string}>}
 */
export async function generateBuilderPdf(payload, meta = {}) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;

    const cyan = toRgb('#1FA5CF');
    const ink = toRgb('#09090B');
    const muted = toRgb('#52525B');

    const builderName = (meta.builderName || '').trim();
    const builderEmail = (meta.builderEmail || '').trim();
    const stamp = (meta.downloadedAt || new Date()).toLocaleString();

    // ---- Cover ----
    doc.setFillColor(...cyan);
    doc.rect(0, 0, pageW, 42, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('Snag Report — Builder Copy', margin, 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated ${stamp}`, margin, 32);

    let y = 56;
    doc.setTextColor(...ink);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(payload.name || 'Property', margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...muted);
    if (payload.address) { doc.text(payload.address, margin, y); y += 5; }
    doc.text(`Homeowner: ${payload.reporter?.name || '—'}`, margin, y); y += 5;
    if (builderName || builderEmail) {
        doc.text(`Recipient: ${[builderName, builderEmail].filter(Boolean).join(' · ')}`, margin, y);
        y += 5;
    }

    // ---- Counts strip ----
    y += 4;
    const all = payload.snags || [];
    const counts = {
        total: all.length,
        outstanding: all.filter(s => s.status === 'outstanding').length,
        completed: all.filter(s => s.status === 'completed').length,
        awaiting: all.filter(s => s.status === 'outstanding' && !s.builder_response).length,
        rejected: all.filter(s => s.builder_response === 'rejected').length,
        disputed: all.filter(s => !!s.disputed).length,
        urgent: all.filter(s => (s.severity || 'minor') === 'urgent').length,
    };
    const tiles = [
        { label: 'Outstanding', value: counts.outstanding, color: [229, 57, 53] },
        { label: 'Awaiting', value: counts.awaiting, color: [23, 135, 171] },
        { label: 'Urgent', value: counts.urgent, color: [153, 27, 27] },
        { label: 'Completed', value: counts.completed, color: [0, 135, 90] },
    ];
    const tileW = (contentW - 6) / 4;
    tiles.forEach((t, i) => {
        const tx = margin + i * (tileW + 2);
        doc.setDrawColor(228, 228, 231);
        doc.rect(tx, y, tileW, 18, 'S');
        doc.setFontSize(7);
        doc.setTextColor(...muted);
        doc.text(t.label.toUpperCase(), tx + 3, y + 5);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(...t.color);
        doc.text(String(t.value), tx + 3, y + 14);
        doc.setFont('helvetica', 'normal');
    });
    y += 26;

    // ---- Sort: urgent → major → minor → cosmetic, outstanding first ----
    const sorted = [...all].sort((a, b) => {
        const sa = a.status === 'outstanding' ? 0 : 1;
        const sb = b.status === 'outstanding' ? 0 : 1;
        if (sa !== sb) return sa - sb;
        const ra = SEV_RANK[a.severity || 'minor'] ?? 9;
        const rb = SEV_RANK[b.severity || 'minor'] ?? 9;
        if (ra !== rb) return ra - rb;
        return (a.pinNumber ?? 9999) - (b.pinNumber ?? 9999);
    });

    // Pre-load all photos so we can lay them out without async waits in the
    // middle of the page-flow loop.
    const photos = await Promise.all(sorted.map(s => loadImageAsDataUrl(s.photo)));

    // ---- Defect list ----
    const rowHeight = 42;
    for (let i = 0; i < sorted.length; i++) {
        const s = sorted[i];
        const photo = photos[i];

        if (y + rowHeight + 12 > pageH - 18) {
            addFooter(doc, builderEmail, stamp, pageW, pageH);
            doc.addPage();
            y = 18;
        }

        // Card border
        doc.setDrawColor(228, 228, 231);
        doc.rect(margin, y, contentW, rowHeight, 'S');

        // Photo (square thumbnail)
        const photoSize = 32;
        const photoX = margin + 4;
        const photoY = y + 5;
        if (photo) {
            try { doc.addImage(photo, 'JPEG', photoX, photoY, photoSize, photoSize); }
            catch { /* skip */ }
        } else {
            doc.setFillColor(244, 244, 245);
            doc.rect(photoX, photoY, photoSize, photoSize, 'F');
            doc.setFontSize(7);
            doc.setTextColor(...muted);
            doc.text('NO PHOTO', photoX + photoSize / 2, photoY + photoSize / 2, { align: 'center' });
        }

        const tx = photoX + photoSize + 5;
        const tw = contentW - (tx - margin) - 30;

        // Pin + title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...ink);
        const pinPrefix = s.pinNumber ? `#${s.pinNumber} · ` : '';
        const titleLines = doc.splitTextToSize(`${pinPrefix}${s.title || '—'}`, tw);
        doc.text(titleLines.slice(0, 2), tx, y + 8);

        // Room + severity badge
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        const roomLine = `${s.room || '—'}${s.floor ? ` · ${s.floor}` : ''}`;
        doc.text(roomLine, tx, y + 18);

        // Severity pill
        const sev = (s.severity || 'minor').toLowerCase();
        const sevColor = SEV_COLOR[sev] || SEV_COLOR.minor;
        doc.setFillColor(...sevColor);
        doc.roundedRect(tx, y + 21, 18, 4.6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.text(sev.toUpperCase(), tx + 9, y + 24.3, { align: 'center' });

        // AI verdict badge (NHBC clause + category)
        const ai = s.ai_analysis;
        if (ai) {
            const aiText = [
                ai.category && `Category: ${ai.category}`,
                ai.nhbc_clause && `NHBC ${ai.nhbc_clause}`,
            ].filter(Boolean).join(' · ');
            if (aiText) {
                doc.setTextColor(...muted);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                const aiLines = doc.splitTextToSize(aiText, tw - 22);
                doc.text(aiLines.slice(0, 1), tx + 22, y + 24.5);
            }
        }

        // Notes (single line, truncated)
        if (s.notes) {
            doc.setFontSize(8);
            doc.setTextColor(...ink);
            doc.setFont('helvetica', 'normal');
            const noteLines = doc.splitTextToSize(s.notes, tw);
            doc.text(noteLines.slice(0, 1), tx, y + 31);
        }

        // Builder response detail (if any)
        if (s.builder_response === 'rejected' && (s.rejection_reason_code || s.rejection_reason_detail)) {
            doc.setFontSize(7);
            doc.setTextColor(229, 57, 53);
            doc.setFont('helvetica', 'italic');
            const reason = [s.rejection_reason_code, s.rejection_reason_detail].filter(Boolean).join(' — ');
            const rLines = doc.splitTextToSize(`Rejected: ${reason}`, tw);
            doc.text(rLines.slice(0, 1), tx, y + 37);
        } else if (s.disputed && s.dispute_reason) {
            doc.setFontSize(7);
            doc.setTextColor(30, 58, 138);
            doc.setFont('helvetica', 'italic');
            const dLines = doc.splitTextToSize(`Disputed: ${s.dispute_reason}`, tw);
            doc.text(dLines.slice(0, 1), tx, y + 37);
        }

        // Status badge (top-right)
        const badge = statusBadge(s);
        const badgeW = 26;
        const badgeX = margin + contentW - badgeW - 4;
        doc.setFillColor(...badge.color);
        doc.roundedRect(badgeX, y + 4, badgeW, 5.2, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(badge.label, badgeX + badgeW / 2, y + 7.7, { align: 'center' });

        y += rowHeight + 3;
    }

    if (sorted.length === 0) {
        doc.setFontSize(11);
        doc.setTextColor(...muted);
        doc.text('No defects on this property yet.', margin, y + 6);
    }

    addFooter(doc, builderEmail, stamp, pageW, pageH);

    // Output: data URL so an <a download> tag works reliably without
    // popup-blocker issues.
    const url = doc.output('datauristring');
    const safeName = (payload.name || 'property').replace(/[^a-z0-9-_ ]/gi, '').trim().replace(/\s+/g, '-').slice(0, 40);
    const filename = `defectr-builder-${safeName || 'report'}-${new Date().toISOString().slice(0, 10)}.pdf`;
    return { url, filename };
}

function addFooter(doc, email, stamp, pageW, pageH) {
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.setFont('helvetica', 'normal');
    const left = `DefectR — Builder copy${email ? ` · ${email}` : ''}`;
    doc.text(left, 14, pageH - 8);
    doc.text(stamp, pageW - 14, pageH - 8, { align: 'right' });
}
